/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useSnackbar } from '@components/snackbar';
import { getMDMentionValues, MinimalEditor } from '@sketchnote/minimal-editor';
import { Button } from '@sketchnote/shared-components';
import { resetEditorValue } from '@sketchnote/slate-plugins';
import { TElement, usePlateEditorRef } from '@udecode/plate';
import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useBoardStore } from 'src/features/boards';
import { MinimalEditorProvider } from 'src/features/collaborative-editor/components/MinimalEditorProvider';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { useAuth } from 'src/shared/hooks/use-auth';
import { Member } from 'src/type';
import { v4 as uuid } from 'uuid';

import { useTasksStore } from '../store/useTasksStore';
import { checkForEmptySlateObjectWithMention } from './utils';

export const ItemAddCommentBase = ({ id, members }: { id: string; members: Member[] }) => {
  const commentEditor = usePlateEditorRef(id)!;

  const [isCommentFocused, setIsCommentFocused] = useState(true);
  const boardsStore = useBoardStore();
  const taskId = useTasksStore((s) => s.currentTaskId);
  const onAddComment = useTasksStore((s) => s.onAddComment);
  const myMemberId = useAuth().userDetails?.memberInformation.memberId;

  const [open] = useSnackbar();

  const [editorValue, setEditorValue] = useState<TElement[]>(intialEditorValue);

  const { isReadOnly } = useTaskAccess(taskId || '');

  const handleEditorChange = (value) => {
    setEditorValue(value);
  };
  const item = useTasksStore(
    useCallback(
      (s) => {
        if (taskId) {
          return s.tasks[taskId];
        }
      },
      [taskId]
    )
  );

  const handleOnComment = async () => {
    const data = checkForEmptySlateObjectWithMention(commentEditor?.children);

    if (data.length > 0) {
      let success = false;
      const mdUsers = getMDMentionValues(commentEditor?.children);

      const myMember = members.find((member) => member.memberId === myMemberId);
      if (!myMember) return;

      if (item?.id) {
        success = await onAddComment({
          comment: commentEditor?.children,
          boardId: boardsStore.id,
          itemId: item.id,
          tags: mdUsers.values,
          text: mdUsers.md,
          member: myMember,
        });
      }
      if (!success) {
        open({
          text: 'Failed to add comment',
          snackType: 'error',
          duration: 3000,
          position: 'top-center',
        });
      }
    }
    resetEditorValue(intialEditorValue, commentEditor);
    setEditorValue(intialEditorValue);
  };

  const handleOnCancel = () => {
    resetEditorValue(intialEditorValue, commentEditor);
    setEditorValue(intialEditorValue);
  };

  const commentInput = useMemo(
    () => (
      <MinimalEditor
        editorValue={editorValue}
        id={id}
        members={members || []}
        disabled={isReadOnly}
        onEditorChange={handleEditorChange}
        onKeyDownHandler={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleOnComment();
          }
        }}
        editableProps={{
          placeholder: 'Add a comment',
          className: 'min-h-48 bg-gray-100 rounded-md p-12 border',
          onFocus: () => setIsCommentFocused(true),
          autoFocus: false,
        }}
      />
    ),
    [id, editorValue]
  );

  return (
    <div className="pb-16 pt-20 h-auto sticky ">
      <div className="flex items-center">
        <div className={cn('w-full')}>
          <DndProvider backend={HTML5Backend}>{commentInput}</DndProvider>
          {isCommentFocused ? (
            <div className="flex mt-10 justify-end">
              <Button status="primary" className="mr-12" onClick={handleOnComment}>
                Comment
              </Button>
              {/* <Button status="secondary" onClick={handleOnCancel}>
                Cancel
              </Button> */}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const intialEditorValue = [{ type: 'p', children: [{ text: '' }] }];

export const ItemAddComment = ({ members }: { members?: Member[] }) => {
  const id = uuid();

  return (
    <MinimalEditorProvider id={id}>
      <ItemAddCommentBase id={id} members={members || []} />
    </MinimalEditorProvider>
  );
};
